import { Box, Grid, Stack } from '@mui/joy';
import { useMemo } from 'react';

import { QuoteEntityType } from 'quotes/enums';
import { useGetQuotesDashboardDetails, useGetQuotesDashboardSummary } from 'reports/hooks';

import { DateRangeFilter } from './DateRangeFilter';
import { QuotesDashboardQuoteType } from './enums';
import { QuotesVsOrdersCard } from './QuotesVsOrdersCard';
import { QuoteTypeFilter } from './QuoteTypeFilter';
import { RecentCustomerQuotesTable } from './RecentCustomerQuotesTable';
import { RecentVendorQuotesTable } from './RecentVendorQuotesTable';
import { StatCards } from './StatCards';
import { useQuotesDashboardFilter } from './useQuotesDashboardFilter';
import { UsersFilter } from './UsersFilter';
import { getQuotesDashboardDateRange } from './utils';
import { VolumeComparisonCard } from './VolumeComparisonCard';

export const QuotesDashboard = () => {
  const { filters, setCorporateUsers, setDateRange, setQuoteType } = useQuotesDashboardFilter();

  const requestPayload = useMemo(
    () => ({
      userIds: filters.corporateUsers?.map((u) => u.userId),
      dateRange: getQuotesDashboardDateRange(filters.dateRange),
    }),
    [filters.corporateUsers, filters.dateRange]
  );

  const {
    summary,
    isLoading: isSummaryLoading,
    isFetching: isSummaryFetching,
  } = useGetQuotesDashboardSummary(requestPayload);

  const {
    details,
    isFetching: isDetailsFetching,
    isLoading: isDetailsLoading,
  } = useGetQuotesDashboardDetails(requestPayload);

  const isSummaryDataLoading = isSummaryLoading || isSummaryFetching;
  const isDetailsDataLoading = isDetailsLoading || isDetailsFetching;

  const isCustomer = filters.quoteType === QuotesDashboardQuoteType.CUSTOMER;

  const corporateUserIds = useMemo(
    () => filters.corporateUsers?.map((user) => user.id) || [],
    [filters.corporateUsers]
  );

  return (
    <Stack gap={2}>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', paddingRight: 1 }}>
        <UsersFilter onChange={setCorporateUsers} value={filters.corporateUsers || []} />
        <DateRangeFilter onChange={setDateRange} value={filters.dateRange} />
        <QuoteTypeFilter onChange={setQuoteType} value={filters.quoteType} />
      </Box>

      <StatCards isLoading={isSummaryDataLoading} data={summary} />

      <Grid container spacing={2}>
        <Grid md={8} xs={12}>
          <Stack gap={2}>
            {isCustomer ? (
              <>
                <RecentCustomerQuotesTable
                  corporateUserIds={corporateUserIds}
                  type={QuoteEntityType.QUOTE}
                />
                <RecentCustomerQuotesTable
                  corporateUserIds={corporateUserIds}
                  type={QuoteEntityType.ORDER}
                />
              </>
            ) : (
              <>
                <RecentVendorQuotesTable
                  corporateUserIds={corporateUserIds}
                  type={QuoteEntityType.QUOTE}
                />
                <RecentVendorQuotesTable
                  corporateUserIds={corporateUserIds}
                  type={QuoteEntityType.ORDER}
                />
              </>
            )}
          </Stack>
        </Grid>
        <Grid md={4} xs={12}>
          <Stack gap={2}>
            <QuotesVsOrdersCard
              data={details}
              dateRange={filters.dateRange}
              isLoading={isDetailsDataLoading}
              type={filters.quoteType}
            />
            <VolumeComparisonCard
              data={summary}
              isLoading={isSummaryDataLoading}
              type={filters.quoteType}
            />
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};
